import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { MicroserviceStatus } from "services/KnowfullyAdminClient";

export const HEALTH_ENDPOINT_PATH = "/api/health?details=true";
export const HEALTH_CHECK_REFETCH_INTERVAL = 30000;

const DETAIL_KEYS = {
  ERROR: "Error",
  RAW_RESPONSE: "RawResponse",
  RESPONSE_CONTENT: "ResponseContent",
  STACK_TRACE: "StackTrace",
  INNER_EXCEPTION: "InnerException",
  HTTP_STATUS: "HttpStatus",
  RESPONSE_READ_ERROR: "ResponseReadError",
  EXCEPTION_TYPE: "ExceptionType",
} as const;

const HEALTH_STATUS_VALUES = {
  HEALTHY: "healthy",
  UNHEALTHY: "unhealthy",
  DEGRADED: "degraded",
  FAILURE: "failure",
} as const;

const useStyles = () => ({
  container: {
    my: 4,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 3,
  },
  serviceCell: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  detailsContainer: {
    p: 2,
    bgcolor: "grey.50",
  },
  detailItem: {
    mb: 1,
  },
  healthCheckCard: {
    mb: 2,
    p: 2,
    border: "1px solid",
    borderRadius: 1,
  },
  healthyCard: {
    borderColor: "success.light",
  },
  unhealthyCard: {
    borderColor: "error.light",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1,
  },
  innerExceptionContainer: {
    pl: 2,
    borderLeft: "2px solid",
    borderColor: "error.light",
  },
  detailTitle: {
    fontWeight: "bold",
    mb: 1,
  },
  detailTitleInline: {
    fontWeight: "bold",
  },
  detailValue: {
    color: "text.secondary",
    ml: 1,
  },
  detailPrimary: {
    fontWeight: "bold",
    color: "text.primary",
  },
  detailSecondary: {
    color: "text.secondary",
  },
  errorContainer: {
    mt: 1,
  },
  errorLabel: {
    fontWeight: "medium",
    color: "error.main",
    mb: 1,
  },
  stackTraceContainer: {
    mt: 2,
  },
  stackTraceLabel: {
    fontWeight: "medium",
    mb: 1,
  },
  innerExceptionLabel: {
    fontWeight: "medium",
  },
  codeBlockWithMargin: {
    mt: 1,
  },
  errorAlert: {
    mt: 2,
  },
  noServicesAlert: {
    mt: 2,
  },
  errorText: {
    fontWeight: "bold",
    color: "error.main",
  },
  codeBlock: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    backgroundColor: "grey.100",
    p: 1,
    borderRadius: 1,
    overflow: "auto",
    whiteSpace: "pre-wrap",
  },
  rawResponseBlock: {
    maxHeight: "200px",
  },
  stackTraceBlock: {
    maxHeight: "300px",
  },
  dashboardButton: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    textTransform: "none",
  },
});

const HealthCheckPage = () => {
  const styles = useStyles();
  const { formatMessage: t } = useIntl();
  const { microservicesStatusService } = useGlobalContext();
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  useListTitle("healthCheckPageTitle");

  const { data, isLoading } = useStrictQuery(
    "getMicroservicesStatus",
    () => microservicesStatusService.getMicroservicesStatus(),
    {
      refetchInterval: HEALTH_CHECK_REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
    }
  );

  const handleRowClick = (name: string) => {
    setExpandedRow(expandedRow === name ? null : name);
  };

  const getStatusIcon = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case HEALTH_STATUS_VALUES.HEALTHY:
        return <CheckCircleIcon color="success" />;
      case HEALTH_STATUS_VALUES.DEGRADED:
        return <WarningIcon color="warning" />;
      case HEALTH_STATUS_VALUES.UNHEALTHY:
      case HEALTH_STATUS_VALUES.FAILURE:
        return <ErrorIcon color="error" />;
      default:
        return <ErrorIcon color="disabled" />;
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case HEALTH_STATUS_VALUES.HEALTHY:
        return "success" as const;
      case HEALTH_STATUS_VALUES.DEGRADED:
        return "warning" as const;
      case HEALTH_STATUS_VALUES.UNHEALTHY:
      case HEALTH_STATUS_VALUES.FAILURE:
        return "error" as const;
      default:
        return "default" as const;
    }
  };

  const getStatusLabel = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case HEALTH_STATUS_VALUES.HEALTHY:
        return t({ id: "healthCheckStatusHealthy" });
      case HEALTH_STATUS_VALUES.DEGRADED:
        return t({ id: "healthCheckStatusDegraded" });
      case HEALTH_STATUS_VALUES.UNHEALTHY:
        return t({ id: "healthCheckStatusUnhealthy" });
      case HEALTH_STATUS_VALUES.FAILURE:
        return t({ id: "healthCheckStatusFailure" });
      default:
        return status;
    }
  };

  const shouldShowClickableUrl = (status: string) => {
    const statusLower = status?.toLowerCase();
    return (
      statusLower === HEALTH_STATUS_VALUES.DEGRADED ||
      statusLower === HEALTH_STATUS_VALUES.UNHEALTHY ||
      statusLower === HEALTH_STATUS_VALUES.FAILURE
    );
  };

  const getHealthDetailsUrl = (baseUrl: string) => {
    return `${baseUrl}${HEALTH_ENDPOINT_PATH}`;
  };

  const formatStackTrace = (stackTrace: string) => {
    if (!stackTrace) return stackTrace;

    return stackTrace
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .join("\n");
  };

  const detailRenderers = {
    [DETAIL_KEYS.RAW_RESPONSE]: (value: any) => (
      <Box sx={styles.detailItem}>
        <Typography variant="subtitle2" sx={styles.detailTitle}>
          Response Content:
        </Typography>
        <Typography
          variant="body2"
          component="pre"
          sx={[styles.codeBlock, styles.rawResponseBlock]}
        >
          {String(value)}
        </Typography>
      </Box>
    ),
    [DETAIL_KEYS.RESPONSE_CONTENT]: (value: any) => (
      <Box sx={styles.detailItem}>
        <Typography variant="subtitle2" sx={styles.detailTitle}>
          Response Content:
        </Typography>
        <Typography
          variant="body2"
          component="pre"
          sx={[styles.codeBlock, styles.rawResponseBlock]}
        >
          {String(value)}
        </Typography>
      </Box>
    ),
    [DETAIL_KEYS.STACK_TRACE]: (value: any) => (
      <Box sx={styles.detailItem}>
        <Typography variant="subtitle2" sx={styles.detailTitle}>
          Stack Trace:
        </Typography>
        <Typography
          variant="body2"
          component="pre"
          sx={[styles.codeBlock, styles.stackTraceBlock]}
        >
          {formatStackTrace(String(value))}
        </Typography>
      </Box>
    ),
    [DETAIL_KEYS.ERROR]: (value: any) => (
      <Box sx={styles.detailItem}>
        <Typography variant="subtitle2" sx={styles.detailTitle}>
          Error Message:
        </Typography>
        <Typography variant="body2" sx={styles.errorText}>
          {String(value)}
        </Typography>
      </Box>
    ),
    [DETAIL_KEYS.HTTP_STATUS]: (value: any) => (
      <Box sx={styles.detailItem}>
        <Typography variant="subtitle2" sx={styles.detailTitleInline}>
          HTTP Status:
        </Typography>
        <Typography variant="body2" sx={styles.detailValue}>
          {String(value)}
        </Typography>
      </Box>
    ),
    [DETAIL_KEYS.INNER_EXCEPTION]: (value: any) => (
      <Box sx={styles.detailItem}>
        <Typography variant="subtitle2" sx={styles.detailTitle}>
          Inner Exception:
        </Typography>
        <Box sx={styles.innerExceptionContainer}>
          <Typography variant="body2" sx={styles.innerExceptionLabel}>
            {value.Type}: {value.Message}
          </Typography>
          {value.StackTrace && (
            <Typography
              variant="body2"
              component="pre"
              sx={[
                styles.codeBlock,
                styles.stackTraceBlock,
                styles.codeBlockWithMargin,
              ]}
            >
              {formatStackTrace(value.StackTrace)}
            </Typography>
          )}
        </Box>
      </Box>
    ),
  };

  const renderHealthCheckResult = (key: string, result: any) => {
    if (!result || typeof result !== "object") {
      return (
        <Box key={key} sx={styles.detailItem}>
          <Typography variant="subtitle2" sx={styles.detailPrimary}>
            {key}
          </Typography>
          <Typography variant="body2" sx={styles.detailSecondary}>
            {String(result)}
          </Typography>
        </Box>
      );
    }

    const status = result.status || "Unknown";
    const isHealthy = status.toLowerCase() === HEALTH_STATUS_VALUES.HEALTHY;
    const cardStyles = [
      styles.healthCheckCard,
      isHealthy ? styles.healthyCard : styles.unhealthyCard,
    ];

    return (
      <Box key={key} sx={cardStyles}>
        <Box sx={styles.cardHeader}>
          {isHealthy ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <ErrorIcon color="error" fontSize="small" />
          )}
          <Typography variant="subtitle2" sx={styles.detailTitleInline}>
            {key}
          </Typography>
          <Chip
            label={status}
            color={isHealthy ? "success" : "error"}
            size="small"
          />
        </Box>

        {result.exception && (
          <Box sx={styles.errorContainer}>
            <Typography variant="body2" sx={styles.errorLabel}>
              Error Message:
            </Typography>
            <Typography variant="body2" sx={styles.errorText}>
              {result.exception.message}
            </Typography>

            {result.exception.stacktrace && (
              <Box sx={styles.stackTraceContainer}>
                <Typography variant="body2" sx={styles.stackTraceLabel}>
                  Stack Trace:
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={[styles.codeBlock, styles.stackTraceBlock]}
                >
                  {formatStackTrace(result.exception.stacktrace)}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const isHealthCheckResult = (value: any): boolean => {
    return typeof value === "object" && value !== null && "status" in value;
  };

  const formatDetails = (details: { [key: string]: any } | undefined) => {
    if (!details) return null;

    return Object.entries(details).map(([key, value]) => {
      const renderer = detailRenderers[key as keyof typeof detailRenderers];

      if (renderer) {
        return <div key={key}>{renderer(value)}</div>;
      }

      if (isHealthCheckResult(value)) {
        return renderHealthCheckResult(key, value);
      }

      return (
        <Box key={key} sx={styles.detailItem}>
          <Typography variant="subtitle2" sx={styles.detailTitleInline}>
            {key}:
          </Typography>
          <Typography variant="body2" sx={styles.detailValue}>
            {typeof value === "object"
              ? JSON.stringify(value, null, 2)
              : String(value)}
          </Typography>
        </Box>
      );
    });
  };

  return (
    <Container>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Typography variant="h4" component="h1">
            <T id="healthCheckPageTitle" />
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DashboardIcon />}
            href="https://portal.azure.com/#@azurecpenow.onmicrosoft.com/dashboard/arm/subscriptions/333cfba5-4ad2-476f-9767-479d7d50ee01/resourcegroups/dashboards/providers/microsoft.portal/dashboards/a7f9017e-e697-4c2c-ac0f-b17bd0ce2b88"
            target="_blank"
            rel="noopener noreferrer"
            sx={styles.dashboardButton}
          >
            <T id="healthCheckAzureDashboard" />
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <T id="healthCheckServiceLabel" />
                </TableCell>
                <TableCell>
                  <T id="healthCheckUrlLabel" />
                </TableCell>
                <TableCell>
                  <T id="healthCheckStatusLabel" />
                </TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={styles.serviceCell}>
                          <Skeleton variant="circular" width={24} height={24} />
                          <Skeleton variant="text" width={120} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={200} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="rounded" width={80} height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="circular" width={24} height={24} />
                      </TableCell>
                    </TableRow>
                  ))
                : data?.data?.map((service: MicroserviceStatus) => (
                    <>
                      <TableRow
                        key={service.name}
                        hover
                        sx={{ cursor: service.details ? "pointer" : "default" }}
                        onClick={() =>
                          service.details && handleRowClick(service.name!)
                        }
                      >
                        <TableCell>
                          <Box sx={styles.serviceCell}>
                            {getStatusIcon(service.status!)}
                            <Typography variant="body2" fontWeight="medium">
                              {service.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {shouldShowClickableUrl(service.status!) ? (
                            <Tooltip title={t({ id: "healthCheckUrlTooltip" })}>
                              <Link
                                href={getHealthDetailsUrl(service.url!)}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                color="primary"
                              >
                                {service.url}
                              </Link>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {service.url}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(service.status!)}
                            color={getStatusColor(service.status!)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {service.details && (
                            <IconButton size="small">
                              {expandedRow === service.name ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                      {service.details && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 0 }}>
                            <Collapse
                              in={expandedRow === service.name}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={styles.detailsContainer}>
                                {formatDetails(service.details)}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>

        {data?.data?.length === 0 && !isLoading && (
          <Alert severity="info" sx={styles.noServicesAlert}>
            <T id="healthCheckNoServicesMessage" />
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default HealthCheckPage;
